import {
  Analytics01Icon,
  Calendar01Icon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  DashboardSquare01Icon,
  DocumentValidationIcon,
  Edit02Icon,
  FingerPrintIcon,
  Megaphone01Icon,
  LegalDocument01Icon,
  Alert02Icon,
  Settings01Icon,
  ShieldKeyIcon,
  UserGroupIcon,
  UserIcon,
} from '@hugeicons/core-free-icons';
import type { IconSvgElement } from '@hugeicons/react';
import type { Permission, Scope } from '@/lib/auth/permissions.js';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: IconSvgElement;
  /** Hidden unless the user holds one of these. Omit to show to everyone. */
  permission?: Permission | readonly Permission[];
  scope?: Scope;
  /**
   * Shown only to somebody who has people reporting to them.
   *
   * Holding the MANAGER role and actually having a team are different things:
   * HR sets a reporting line without granting a role, and a role can be held
   * by somebody who manages nobody yet. A "My Team" link that leads to an
   * empty page — or one that is hidden from a person who does have a team —
   * are both wrong, and a permission check answers neither question.
   */
  requiresTeam?: boolean;
}

export interface NavSection {
  /** Omit for the first, unlabelled group. */
  title?: string;
  items: NavItem[];
}

/**
 * The navigation tree — and, in effect, the product's information
 * architecture.
 *
 * Grouped the way Atlassian groups things: by *who you are being* right now,
 * not by which backend module owns the data. An employee checking their own
 * leave and a manager approving someone else's are different jobs, so they are
 * different sections, even though both are "leave".
 *
 * Sections disappear entirely when the user holds none of their permissions,
 * so a standard employee sees four items rather than four items and three
 * locked padlocks. Nielsen's aesthetic-and-minimalist-design heuristic: every
 * extra item competes for attention with the ones that matter.
 *
 * This is presentation only — the server enforces every one of these
 * permissions again.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        key: 'dashboard',
        label: 'Home',
        to: '/',
        icon: DashboardSquare01Icon,
      },
    ],
  },
  {
    title: 'Me',
    items: [
      {
        key: 'profile',
        label: 'My Profile',
        to: '/profile',
        icon: UserIcon,
      },
      {
        key: 'attendance',
        label: 'Attendance',
        to: '/attendance',
        icon: Clock01Icon,
        permission: 'attendance:read',
      },
      {
        key: 'leave',
        label: 'Leave',
        to: '/leave',
        icon: Calendar01Icon,
        permission: 'leave:read',
      },
      {
        key: 'calendar',
        label: 'Holidays',
        to: '/holidays',
        icon: Calendar01Icon,
        permission: 'holiday:read',
      },
      {
        key: 'security',
        label: 'Security',
        to: '/settings/security',
        icon: FingerPrintIcon,
      },
    ],
  },
  {
    title: 'My Team',
    items: [
      {
        key: 'approvals',
        label: 'Approvals',
        to: '/team/approvals',
        icon: CheckmarkCircle02Icon,
        permission: ['leave:approve', 'regularization:approve'],
        scope: 'team',
      },
      {
        key: 'team',
        label: 'Team',
        to: '/team',
        icon: UserGroupIcon,
        requiresTeam: true,
      },
    ],
  },
  {
    title: 'Company',
    items: [
      {
        key: 'directory',
        label: 'Directory',
        to: '/directory',
        icon: UserGroupIcon,
        permission: 'directory:read',
        scope: 'org',
      },
      {
        key: 'announcements',
        label: 'Announcements',
        to: '/announcements',
        icon: Megaphone01Icon,
        permission: 'announcement:read',
      },
      {
        key: 'policies',
        label: 'Policies',
        to: '/policies',
        icon: DocumentValidationIcon,
        permission: 'policy:read',
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        key: 'admin-console',
        label: 'Console',
        to: '/admin/console',
        icon: DashboardSquare01Icon,
        permission: 'report:view',
        scope: 'team',
      },
      {
        key: 'admin-employees',
        label: 'Employees',
        to: '/admin/employees',
        icon: UserGroupIcon,
        permission: 'employee:update',
        scope: 'org',
      },
      {
        key: 'admin-change-requests',
        label: 'Change Requests',
        to: '/admin/change-requests',
        icon: Edit02Icon,
        permission: 'changeRequest:approve',
        scope: 'org',
      },
      {
        key: 'admin-announcements',
        label: 'Announcements',
        to: '/admin/announcements',
        icon: Megaphone01Icon,
        permission: 'announcement:manage',
        scope: 'org',
      },
      {
        key: 'admin-policies',
        label: 'Policies',
        to: '/admin/policies',
        icon: LegalDocument01Icon,
        permission: 'policy:manage',
        scope: 'org',
      },
      {
        key: 'admin-calendar',
        label: 'Holiday Calendar',
        to: '/admin/holidays',
        icon: Calendar01Icon,
        permission: 'holiday:manage',
        scope: 'org',
      },
      {
        key: 'admin-separations',
        label: 'Separations',
        to: '/admin/separations',
        icon: UserIcon,
        permission: 'employee:update',
        scope: 'org',
      },
      {
        key: 'admin-reports',
        label: 'Reports',
        to: '/admin/reports',
        icon: Analytics01Icon,
        permission: 'report:view',
        scope: 'team',
      },
      {
        key: 'admin-settings',
        label: 'Settings',
        to: '/admin/settings',
        icon: Settings01Icon,
        permission: ['department:manage', 'role:grant'],
        scope: 'org',
      },
      {
        /**
         * Super Admin only. The trail is the record *of* administrative
         * action, and most of that action is HR's — somebody who can both act
         * and curate the record of it is one role where there should be two.
         */
        key: 'admin-audit',
        label: 'Audit Log',
        to: '/admin/audit',
        icon: ShieldKeyIcon,
        permission: 'audit:read',
        scope: 'org',
      },
    ],
  },
  {
    /**
     * Its own section, not a row under "Company".
     *
     * Filing a harassment complaint should not sit between the staff
     * directory and the holiday calendar, and the committee's case log is not
     * an administrative screen — putting it under "Administration" would
     * invite somebody to give the admin role a peek at it one day.
     */
    title: 'POSH',
    items: [
      {
        key: 'posh-report',
        label: 'Report a Concern',
        to: '/report-a-concern',
        icon: Alert02Icon,
        permission: 'posh:submit',
      },
      {
        /**
         * Gated on `posh:read`, which only POSH_OFFICER holds — so this is
         * invisible to HR and to Super Admins, which is the point. Hiding it
         * is a courtesy; the API refuses them regardless.
         */
        key: 'posh-cases',
        label: 'Grievance Cases',
        to: '/posh/cases',
        icon: ShieldKeyIcon,
        permission: 'posh:read',
        scope: 'org',
      },
    ],
  },
];

/**
 * Whether somebody may go to a destination.
 *
 * The sidebar hides links with this, and the route guard refuses them with
 * the same call. Two implementations is how a hidden link still opens the
 * page when the URL is typed — which is exactly what it did.
 *
 * Presentation only, in the sense that it decides what to draw. The server
 * enforces every one of these permissions again, and is the only thing that
 * actually protects the data.
 */
export function isPermitted(
  item: Pick<NavItem, 'permission' | 'scope' | 'requiresTeam'>,
  access: {
    can: (permission: Permission, scope?: Scope) => boolean;
    canAny: (permissions: readonly Permission[], scope?: Scope) => boolean;
    managesPeople: boolean;
  },
): boolean {
  if (item.requiresTeam && !access.managesPeople) return false;
  if (!item.permission) return true;

  return Array.isArray(item.permission)
    ? access.canAny(item.permission, item.scope)
    : access.can(item.permission as Permission, item.scope);
}

/** Every destination by path, so one can be looked up from a URL. */
const BY_PATH = new Map<string, NavItem>(
  NAV_SECTIONS.flatMap((section) => section.items).map((item) => [item.to, item]),
);

/**
 * What a URL requires, if the navigation declares it.
 *
 * A path with no entry — a detail page reached from a list, say — has no
 * requirement here and is left to the screen and the server. This map is the
 * menu, not a security boundary.
 */
export function requirementFor(pathname: string): NavItem | undefined {
  const normalised =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return BY_PATH.get(normalised);
}
